
import React from 'react';
import { supabase, getAuthRedirectUrl } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { analytics } from '@/services/analytics/analyticsService';

interface GoogleAuthProps {
  setIsLoading: (loading: boolean) => void;
  setUser: (user: any) => void;
  setSession: (session: any) => void;
  setIsAdmin: (isAdmin: boolean) => void;
}

export const useGoogleAuth = ({ setIsLoading, setUser, setSession }: GoogleAuthProps) => {
  const signInWithGoogle = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Track auth attempt initiation
      analytics.trackAuthEvent('sign_in', 'google');
      
      // Set a more detailed flag to track the Google Auth flow
      const timestamp = Date.now().toString();
      sessionStorage.setItem('googleAuthInProgress', timestamp);
      console.log('Starting Google sign-in flow, timestamp:', timestamp);
      
      // Get the redirect URL for the OAuth flow
      const redirectTo = getAuthRedirectUrl('signin');
      console.log('Using Google auth redirect URL:', redirectTo);
      
      // Ensure the redirect URL is correct and matches what's configured in Supabase
      if (!redirectTo.includes('/auth/callback')) {
        console.error('Redirect URL does not contain /auth/callback path:', redirectTo);
        toast.error('Invalid redirect configuration');
        setIsLoading(false);
        
        // Track error
        analytics.trackAuthEvent('sign_in', 'google');
        
        return false;
      }
      
      // Log current URL to debug
      const currentUrl = window.location.href;
      const domain = window.location.origin;
      console.log('Current domain:', domain);
      console.log('Current URL before redirect:', currentUrl);
      
      // Explicitly set provider in case it's not being tracked correctly
      sessionStorage.setItem('authProvider', 'google');
      
      // Initiate the Google sign-in flow with more explicit options
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account' // Force account selection each time
          },
          skipBrowserRedirect: false // Ensure browser redirects
        }
      });
      
      // Check for errors in the initial OAuth request
      if (error) {
        console.error('Google sign-in error:', error);
        toast.error('Failed to connect to Google', {
          description: error.message
        });
        
        // Track error
        analytics.trackAuthEvent('sign_in', 'google');
        
        setIsLoading(false);
        sessionStorage.removeItem('googleAuthInProgress');
        sessionStorage.removeItem('authProvider');
        return false;
      }
      
      // Google OAuth flow is initiated
      if (data?.url) {
        console.log('Google sign-in URL generated, will redirect to:', data.url);
        toast.info('Redirecting to Google...');
        
        // Track redirect
        analytics.trackEvent({
          category: 'User',
          action: 'google_auth_redirect',
          label: 'Auth Initiated'
        });
        
        return true;
      } else {
        console.error('No redirect URL returned from Google auth initiation');
        toast.error('Failed to generate Google authentication URL');
        
        // Track error
        analytics.trackAuthEvent('sign_in', 'google');
        
        setIsLoading(false);
        sessionStorage.removeItem('googleAuthInProgress');
        sessionStorage.removeItem('authProvider');
        return false;
      }
    } catch (error: any) {
      console.error('Error in Google sign-in:', error);
      toast.error('Google sign-in failed', {
        description: error.message || 'An unexpected error occurred'
      });
      
      // Track error
      analytics.trackAuthEvent('sign_in', 'google');
      
      setIsLoading(false);
      sessionStorage.removeItem('googleAuthInProgress');
      sessionStorage.removeItem('authProvider');
      return false;
    }
  };

  return { signInWithGoogle };
};
